import React, { useState, useEffect } from 'react';
import { Table, Button, Space, message, Input } from 'antd';
import { SearchOutlined, ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

interface DownloadRecordItem {
  id: number;
  name: string;
  project_no: string;
  project_name: string;
  supplier_name: string | null;
  download_time: string;
}

export default function DownloadRecord() {
  const [records, setRecords] = useState<DownloadRecordItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');

  useEffect(() => {
    fetchRecords();
  }, []);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams();
      if (searchKeyword) {
        params.append('keyword', searchKeyword);
      }
      
      const resp = await fetch(`/api/admin/download-records?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        setRecords(resp.data || []);
      } else {
        message.error(resp?.error || '获取数据失败');
      }
    } catch (err) {
      message.error('获取数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchRecords();
  };

  // 格式化时间
  const formatDateTime = (time: string | null) => {
    if (!time) return '-';
    return dayjs(time).format('YYYY-MM-DD HH:mm:ss');
  };

  const columns: ColumnsType<DownloadRecordItem> = [
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      width: 150,
    },
    {
      title: '下载文件项目编号',
      dataIndex: 'project_no',
      key: 'project_no',
      width: 200,
      render: (text: string) => (
        <span style={{ fontFamily: 'monospace', color: '#1f2937' }}>{text || '-'}</span>
      ),
    },
    {
      title: '下载文件项目名称',
      dataIndex: 'project_name',
      key: 'project_name',
      width: 300,
      ellipsis: true,
      render: (text: string) => (
        <span style={{ fontWeight: 500, color: '#1f2937' }} title={text}>
          {text || '-'}
        </span>
      ),
    },
    {
      title: '下载文件所属公司',
      dataIndex: 'supplier_name',
      key: 'supplier_name',
      width: 200,
      ellipsis: true,
      render: (text: string | null) => (
        <span title={text || ''}>{text || '-'}</span>
      ),
    },
    {
      title: '下载文件时间',
      dataIndex: 'download_time',
      key: 'download_time',
      width: 180,
      render: (text: string) => (
        <span style={{ color: '#6b7280', fontSize: 13 }}>{formatDateTime(text)}</span>
      ),
    },
  ];

  // 根据搜索关键词过滤数据（前端过滤作为备用方案）
  const filteredData = searchKeyword
    ? records.filter(
        (item) =>
          item.name?.includes(searchKeyword) ||
          item.project_no?.includes(searchKeyword) ||
          item.project_name?.includes(searchKeyword)
      )
    : records;

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div className="admin-page-title">
          <div className="admin-page-title-icon">
            <DownloadOutlined />
          </div>
          <div>
            <h2>下载记录</h2>
            <p>查看用户下载文件记录</p>
          </div>
        </div>
        
        <Space size="middle">
          <Input
            placeholder="搜索姓名/项目编号/项目名称"
            prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 280, borderRadius: 10 }}
            allowClear
          />
          <Button 
            icon={<ReloadOutlined />} 
            onClick={fetchRecords}
            style={{ borderRadius: 10 }}
          >
            刷新
          </Button>
        </Space>
      </div>
      
      {/* 表格内容 */}
      <div className="admin-page-content">
        <div style={{ marginBottom: 16, color: '#6b7280', fontSize: 14 }}>
          共 <span style={{ color: '#3b82f6', fontWeight: 600 }}>{filteredData.length}</span> 条记录
        </div>
        
        <Table
          columns={columns}
          dataSource={filteredData}
          loading={loading}
          rowKey="id"
          className="admin-table"
          scroll={{ x: 1030 }}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          }}
        />
      </div>
    </div>
  );
}

